<template>
  <router-link
    :to="$buildContentLink(linkContentType, linkContentItem)"
    :class="[
      'training-history__item',
      isCompleted && 'training-history__item--completed',
      isFetching && 'training-history__item--loading',
    ]"
  >
    <div v-if="isCompleted" class="training-history__completed-circle">
      <CheckboxTick />
    </div>
    <div class="training-history__name-container">
      <div class="training-history__icon">
        <component :is="contentIcon" />
      </div>
      <div class="training-history__name-wrapper">
        <span v-if="isCTFLab" class="training-history__ctf">CTF</span>
        <p class="training-history__name">{{ contentItem.name }}</p>
        <div class="training-history__date">{{ getTimeInDaysAgo }}</div>
        <div class="training-history__progress-container--mobile">
          <div
            v-if="showProgress"
            class="training-history__statistics-container training-history__statistics-container--mobile"
          >
            <div
              v-if="!isCompleted || !isCOCEligible"
              class="training-history__progress training-history__progress--mobile"
            >
              <div
                class="dashboard__progress-wrapper training-history__progress-wrapper training-history__progress-wrapper--mobile"
              >
                <div
                  class="dashboard__progress-bar"
                  :style="`width: ${progress}%`"
                ></div>
              </div>
              <div class="training-history__progress-label">
                {{ progress }}%
              </div>
            </div>
          </div>
          <div v-else class="training-history__progress-no-trackable-container">
            <div class="training-history__progress-no-trackable-text">
              Started
            </div>
          </div>
        </div>
        <div v-if="tooltipText" class="training-history__footer-mobile">
          *{{ tooltipText }}
        </div>
        <div v-if="hasLearningPath" class="training-history__lp">
          <span class="training-history__lp-title"
            >This course appears in the Learning Path(s):</span
          >
          <span
            ref="historyLp"
            v-tooltip="
              showLearningPathTooltip && {
                content: lpTooltip,
                placement: 'right-center',
                classes: 'tooltip__text-centered tooltip__w270',
              }
            "
            class="training-history__learning-paths"
          >
            <router-link
              v-for="(lp, index) in contentItem.learning_paths"
              :key="index"
              :to="lpLink(lp)"
            >
              {{ learningPathLabel(lp, index) }}
            </router-link>
          </span>
        </div>
      </div>
    </div>
    <div
      :class="[
        'training-history__statistics-container',
        statisticsHasCta && 'training-history__statistics-container--has-cta',
      ]"
    >
      <div class="training-history__progress-container">
        <div v-if="showProgress" class="training-history__status">
          <div
            v-if="!isCompleted || !isCOCEligible"
            class="training-history__progress"
          >
            <div
              class="dashboard__progress-wrapper training-history__progress-wrapper"
            >
              <div
                class="dashboard__progress-bar"
                :style="`width: ${progress}%`"
              ></div>
            </div>
            <div class="training-history__progress-label">{{ progress }}%</div>
          </div>
          <div
            v-else-if="isCOCEligible && isAfterMacDate"
            v-tooltip="
              isCoCDisabled &&
                !isCertificateLoading && {
                  content: retryText,
                  placement: 'right-center',
                  classes: 'tooltip__text-centered tooltip__w240',
                }
            "
            class="training-history__cta-container"
          >
            <button
              v-if="!isCertificateLoading"
              class="
                btn
                btn--secondary
                btn--secondary--with-icon
                btn--secondary--with-icon--right
                btn--default-icon-color"
              :class="[
                'training-history__cta-wrapper',
                !contentItem.is_certificate_of_completion_eligible &&
                  'training-history__cta-wrapper--disabled',
              ]"
              :disabled="isCoCDisabled"
              @click="handleCertificateClick"
            >
              <MailIcon />
              <div class="training-history__cta-label">
                Certificate of completion
              </div>
            </button>
            <ActionLoader
              v-else
              class="training-history__cta-container--loading"
            />
          </div>
        </div>
        <div v-else class="training-history__progress-no-trackable-container">
          <div class="training-history__progress-no-trackable-text">
            Started
          </div>
        </div>
      </div>
      <InfoIcon
        v-if="showInfoIcon"
        v-tooltip="{
          content: tooltipText,
          placement: 'right-center',
          classes: 'tooltip__text-centered tooltip__w240',
        }"
        class="training-history__info-icon"
      />
    </div>
  </router-link>
</template>

<script>
import { formatDistance } from 'date-fns'
import { InfoIcon, MailIcon } from 'vue-feather-icons'
import CourseIcon from '@/assets/icons/contentTypes/course-icon.svg'
import LearningPathIcon from '@/assets/icons/contentTypes/learning-path-icon.svg'
import SkillDiveCTFIcon from '@/assets/icons/contentTypes/skill-dive-ctf.svg'
import SkillDiveIcon from '@/assets/icons/contentTypes/skill-dive.svg'
import SkillCheckLabIcon from '@/assets/icons/contentTypes/skill-check-lab.svg'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CONSTANTS from '@/constants'
import { mapActions } from 'vuex'

const {
  COURSE,
  SKILL_DIVE,
  SKILL_CHECK,
  LEARNING_PATH,
} = CONSTANTS.ANALYTICS.HISTORY_CONTENT_TYPES

export default {
  name: 'TrainingHistoryTile',
  components: {
    ActionLoader,
    CourseIcon: { ...CourseIcon, name: 'CourseIcon' },
    LearningPathIcon: { ...LearningPathIcon, name: 'LearningPath' },
    SkillDiveCTF: { ...SkillDiveCTFIcon, name: 'SkillDiveCTFIcon' },
    SkillDiveIcon: { ...SkillDiveIcon, name: 'SkillDiveIcon' },
    SkillCheckLabIcon: { ...SkillCheckLabIcon, name: 'SkillCheckLabIcon' },
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
    MailIcon,
    InfoIcon,
  },
  props: {
    contentItem: {
      type: Object,
      required: true,
    },
    contentType: {
      type: String,
      required: true,
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    windowWidth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isCertificateLoading: false,
      showLearningPathTooltip: false,
      cocError: null,
    }
  },
  computed: {
    getTimeInDaysAgo() {
      const time = this.contentItem.last_active_at ?? ''
      if (time && time !== 'None') {
        const distance = `${formatDistance(new Date(), new Date(time))} ago`
        return distance.charAt(0).toUpperCase() + distance.slice(1)
      }
      return ''
    },
    contentStatus() {
      if (this.isLab) return this.contentItem.progress
      return this.contentItem.status
    },
    showProgress() {
      if (this.contentType !== SKILL_DIVE) return true
      const { STARTED } = CONSTANTS.ANALYTICS.LAB_CONTENT_STATUS
      return this.isCTFLab && this.contentStatus !== STARTED
    },
    isCompleted() {
      const { COMPLETED } = CONSTANTS.ANALYTICS.CONTENT_STATUS
      const { COMPLETE } = CONSTANTS.ANALYTICS.LAB_CONTENT_STATUS
      return [COMPLETED, COMPLETE].includes(this.contentStatus)
    },
    contentIcon() {
      switch (this.contentType) {
        case COURSE:
          return CourseIcon
        case SKILL_DIVE:
          return this.isCTFLab ? SkillDiveCTFIcon : SkillDiveIcon
        case SKILL_CHECK:
          return SkillCheckLabIcon
        case LEARNING_PATH:
          return LearningPathIcon
        default:
          return CourseIcon
      }
    },
    linkContentItem() {
      let item = [SKILL_CHECK, SKILL_DIVE].includes(this.contentType)
        ? {
            ...this.contentItem,
            objectID: this.contentItem.id,
          }
        : this.contentItem
      return item
    },
    linkContentType() {
      switch (this.contentType) {
        case LEARNING_PATH:
          return 'learning-path'
        case SKILL_DIVE:
        case SKILL_CHECK:
          return 'lab'
        default:
          return this.contentType
      }
    },
    hasLearningPath() {
      return (
        this.contentType === COURSE && this.contentItem?.learning_paths?.length
      )
    },
    progress() {
      const { progress, score } = this.contentItem
      const value = Number.isFinite(progress) ? progress : score
      return value === 1 ? 100 : Math.floor(value * 100)
    },
    isCOCEligible() {
      return this.contentItem?.is_certificate_of_completion_eligible
    },
    isAfterMacDate() {
      return this.contentItem?.started_after_mark_as_completed
    },
    isCTFLab() {
      return this.contentItem?.is_ctf
    },
    isSkillCheck() {
      return this.contentItem?.type === SKILL_CHECK
    },
    learningPathChars() {
      return this.contentItem?.learning_paths.reduce(
        (acc, lp) => acc + lp.name.length,
        0
      )
    },
    showInfoIcon() {
      if (!this.isAfterMacDate) return true
      if (this.isCompleted) return false
      return this.isCTFLab || this.isSkillCheck
    },
    lpTooltip() {
      return this.contentItem?.learning_paths
        .map(lp => lp.name)
        .join('; ')
        .slice(0, 100)
    },
    tooltipText() {
      if (!this.isAfterMacDate)
        return 'Certificates of Completion cannot be awarded for content started prior to October 2nd, 2024'
      if (this.isCTFLab && !this.isCompleted)
        return 'Capture all the flags successfully to earn certification of completion'
      if (this.isSkillCheck && !this.isCompleted)
        return 'Score 100% to earn certification of completion'
      return ''
    },
    statisticsHasCta() {
      return this.showProgress && this.isCompleted && this.isCOCEligible
    },
    cocErrorText() {
      return this.cocError?.message
    },
    isCoCDisabled() {
      return this.cocError?.retry_at
    },
    isLab() {
      return [SKILL_DIVE, SKILL_CHECK].includes(this.contentType)
    },
    retryText() {
      const retry_at = this.cocError?.retry_at
      if (!retry_at) return ''

      const now = new Date()
      const retryTime = new Date(retry_at)

      const nowInUTC = new Date(now.toISOString())

      const timeDiffMinutes = Math.ceil(
        (retryTime.getTime() - nowInUTC.getTime()) / 60000
      )

      let count = timeDiffMinutes
      let unit = 'minute'

      if (timeDiffMinutes > 72 * 60) {
        count = Math.ceil(timeDiffMinutes / (60 * 24))
        unit = 'day'
      } else if (timeDiffMinutes >= 60) {
        count = Math.ceil(timeDiffMinutes / 60)
        unit = 'hour'
      }

      return `Wait ${count} ${this.$pluralizeWord(
        unit,
        count
      )} before requesting again`
    },
  },
  watch: {
    windowWidth() {
      this.setLearningPathTooltip()
    },
  },
  mounted() {
    this.setLearningPathTooltip()
  },
  methods: {
    ...mapActions({
      requestCertificateCompletion: 'content/requestCertificateCompletion',
      setSuccess: 'app/setSuccess',
      setError: 'app/setError',
    }),
    async handleCertificateClick(event) {
      event.stopPropagation()
      event.preventDefault()
      if (!this.isCOCEligible) return
      try {
        this.isCertificateLoading = true
        await this.requestCertificateCompletion({
          content_type: this.contentType,
          content_id: this.contentItem.id,
        })
        this.setSuccess('You successfully requested the certification', {
          root: true,
        })
      } catch (error) {
        this.cocError = error
        const { message, http_code } = error
        let errorMessage = http_code !== 429 ? message : this.retryText
        this.setError(errorMessage, {
          root: true,
        })
        this.isCertificateLoading = false
      } finally {
        this.isCertificateLoading = false
      }
    },
    lpLink(lp) {
      const type = 'learning-path'
      return this.$buildContentLink(type, lp)
    },
    learningPathLabel(lp, index) {
      return `${lp.name}${
        index < this.contentItem?.learning_paths.length - 1 ? '; ' : ''
      }`
    },
    setLearningPathTooltip() {
      const element = this.$refs.historyLp
      if (element) {
        this.showLearningPathTooltip = element.scrollWidth > element.offsetWidth
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.training-history {
  &__item {
    display: flex;
    gap: 10px;
    position: relative;
    min-height: 52px;
    flex-wrap: nowrap;
    background: var(--glass-mid-gray);
    margin: 5px 0;
    padding: 8px 15px;
    text-decoration: none;
    border-radius: 5px;
    cursor: pointer;
    border-left: 6px solid var(--tundora-gray);
    &:hover {
      opacity: 0.8;
    }
    @include mobile {
      align-items: center;
    }
    &--completed {
      border-left-color: var(--positive-light);
    }
    &--loading {
      pointer-events: none;
      opacity: 0.5;
    }
  }
  &__completed-circle {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--positive-light);
    left: -13px;
    top: 50%;
    transform: translateY(-50%);
    svg {
      width: 12px;
      path {
        stroke: var(--tundora-gray);
      }
    }
  }
  &__name-container {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 1 100%;
  }
  &__name-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    @include mobile {
      flex: 1 1 100%;
      margin: 0;
    }
  }
  &__icon {
    display: none;
    @include mobile {
      display: flex;
      width: 24px;
      height: 24px;
    }
  }
  &__ctf {
    color: var(--orange-dark);
    font-size: rem(7);
    line-height: rem(12);
    font-weight: 700;
    @include mobile {
      display: none;
    }
  }
  &__name {
    color: var(--white);
    font-size: rem(14);
    line-height: 20px;
    margin: 0;
  }
  &__date,
  &__lp {
    display: block;
    color: var(--light-blue-gray);
    font-size: rem(10);
    line-height: rem(12);
    margin-top: 5px;
    ::v-deep a {
      color: var(--light-blue-gray);
      text-decoration: underline;
    }
    @include mobile {
      display: flex;
      flex-wrap: nowrap;
    }
  }
  &__lp-title {
    flex: 0 0 197px;
  }
  &__learning-paths {
    @include mobile {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &__statistics-container {
    display: none;
    @include mobile {
      display: flex;
      flex: 0 0 220px;
      justify-content: flex-end;
      align-items: center;
      margin-left: auto;
      padding: 8px 0 8px 8px;
      gap: 5px;
    }
    &--mobile {
      display: flex;
      width: 100%;
      @include mobile {
        display: none;
      }
    }
    &--has-cta {
      display: flex;
    }
  }

  &__progress-wrapper {
    display: flex;
    align-items: center;
    margin-right: 10px;
    width: 183px;
    &--mobile {
      width: 100%;
      @include mobile {
        width: inherit;
      }
    }
  }
  &__progress-container {
    display: flex;
    align-items: center;
    &--mobile {
      display: flex;
      width: 100%;
      @include mobile {
        display: none;
      }
    }
  }
  &__progress {
    display: none;
    @include mobile {
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;
    }
    &--mobile {
      display: flex;
      align-items: center;
      margin-top: 5px;
      width: 100%;
      @include mobile {
        display: none;
      }
    }
  }
  &__progress-no-trackable {
    &-container {
      display: flex;
      align-items: center;
    }
    &-text {
      font-size: rem(12);
      line-height: rem(14);
      color: var(--perfect-gray);
    }
  }
  &__progress-label {
    font-size: rem(12);
    line-height: rem(14);
    color: var(--perfect-gray);
  }
  &__info-icon {
    display: none;
    @include mobile {
      display: block;
      color: var(--perfect-gray);
      width: 12px;
      height: 12px;
      &-tooltip {
        text-align: center;
      }
    }
  }
  &__cta-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--white);
    cursor: pointer;
    height: 40px;
    width: 40px;
    padding: 0;
    svg {
      width: 16px;
      height: 16px;
    }
    &--disabled {
      opacity: 0.5;
      cursor: initial;
    }
    @include mobile {
      height: 20px;
      width: fit-content;
      border: none;
    }
  }
  &__cta-label {
    display: none;
    @include mobile {
      display: block;
      font-size: rem(14);
    }
  }
  &__cta-container {
    &--loading {
      height: 20px;
      display: flex;
      align-items: center;
      ::v-deep .action-loader__loader {
        transform: scale(0.5) !important;
      }
    }
  }
  &__footer-mobile {
    display: block;
    font-size: rem(10);
    line-height: rem(12);
    font-style: italic;
    color: var(--light-blue-gray);
    margin-top: 2px;
    @include mobile {
      display: none;
    }
  }
}

.mobile {
  display: block;
  @include mobile {
    display: none;
  }
}
</style>
